import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  ContextualMenu,
  FontWeights,
  getTheme,
  IBasePickerSuggestionsProps,
  IconButton,
  IDragOptions,
  IIconProps,
  Image,
  IPersona,
  IStackItemStyles,
  IStackStyles,
  IStackTokens,
  Label,
  mergeStyleSets,
  Modal,
  NormalPeoplePicker,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  TextField,
  ValidationState,
} from '@fluentui/react';
import React, { useEffect, useRef, useState } from 'react';
import { useBoolean, useId } from '@uifabric/react-hooks';
import _ from 'lodash';
import { uPrinceTheme } from '../../../../../theme';
import { InstructionLink } from '../../../../types/projectBreakdownStructure';
import { getFileIconName } from '../../../../shared/util';
import ProgressBar from '../../../../shared/progressBar/progressBar';
import SignatureCanvas from 'react-signature-canvas';

const theme = getTheme();
const iconButtonStyles = {
  root: {
    color: uPrinceTheme.palette.themePrimary,
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};

const classNames = mergeStyleSets({
  wrapper: {
    //height: '100vh',
    position: 'relative',
    maxHeight: 'inherit',
  },
  pane: {
    maxWidth: 400,
  },
  textContent: {
    padding: '15px 10px',
  },
  actionIcon: {
    padding: 5,
    cursor: 'pointer',
  },
  labelRightWrapper: {
    textAlign: 'end',
    selectors: {
      '@media(max-width: 1100px)': {
        textAlign: 'start',
      },
    },
  },
  actionButton: {
    color: uPrinceTheme.palette.themePrimary,
    cursor: 'pointer',
  },
  labelRightIcon: {
    display: 'inline-block',
    verticalAlign: 'bottom',
    paddingBottom: 2,
    paddingRight: 5,
  },
  fullWidth: {
    width: '100%',
  },
  fullWidthCol: {
    width: '100%',
    flexGrow: '',
  },
});

const stackStyles: IStackStyles = {
  root: {
    padding: 0,
  },
};
const stackItemStyles: IStackItemStyles = {
  root: {
    display: 'flex',
    height: 50,
    paddingBottom: 10,
    flexGrow: 1,
    width: 0,
  },
};

const signPadItemStyles: IStackItemStyles = {
  root: {
    display: 'flex',
    height: 250,
    paddingBottom: 10,
    flexGrow: 1,
    width: 0,
  },
};

const dragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
};

const cancelIcon: IIconProps = { iconName: 'Cancel' };

const addWhiteIconButtonStyles = {
  root: {
    color: uPrinceTheme.palette.white,
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};

const addIcon: IIconProps = { iconName: 'Add', styles: iconButtonStyles };

const addIconWhite: IIconProps = {
  iconName: 'Add',
  styles: addWhiteIconButtonStyles,
};

const contentStyles = mergeStyleSets({
  container: {
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'stretch',
  },
  actionButtonLabel: {
    color: uPrinceTheme.palette.themePrimary,
  },
  header: [
    // eslint-disable-next-line deprecation/deprecation
    theme.fonts.xLargePlus,
    {
      flex: '1 1 auto',
      display: 'flex',
      alignItems: 'center',
      fontWeight: FontWeights.semibold,
      padding: '2px 2px 2px 10px',
      backgroundColor: uPrinceTheme.palette.themePrimary,
      fontSize: 18,
      color: 'white',
    },
  ],
  footer: [
    // eslint-disable-next-line deprecation/deprecation
    theme.fonts.xLargePlus,
    {
      flex: '1 1 auto',
      alignItems: 'center',
      fontWeight: FontWeights.semibold,
      padding: '0px 24px 14px 24px',
      textAlign: 'end',
    },
  ],
  body: {
    flex: '4 4 auto',
    padding: '0 24px 0px 24px',
    overflowY: 'hidden',
    minWidth: '40vw',
    minHeight: '20vh',
    selectors: {
      p: { margin: '14px 0' },
      'p:first-child': { marginTop: 0 },
      'p:last-child': { marginBottom: 0 },
    },
  },
  subHeader: {
    flex: '1 1 auto',
    display: 'block',
    alignItems: 'center',
    fontWeight: FontWeights.semibold,
    padding: '2px 2px 2px 10px',
    fontSize: 14,
    color: uPrinceTheme.palette.themePrimary,
    backgroundColor: '#FFF7F4',
    height: 40,
    marginLeft: 'auto',
    marginRight: '2px',
  },
});
const cancelIconButtonStyles = {
  root: {
    marginLeft: 'auto',
    marginTop: '4px',
    marginRight: '2px',
    color: 'white',
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};

const stackTokens: IStackTokens = {
  childrenGap: 10,
  padding: 10,
};

class SignatureValidationMessages {
  linkValidationMsg: string = '';
  nameValidationMsg: string = '';
}

export class Signature {
  name: string | null = null;
  link: string | null = null;
  pmolId: string | null = null;
}

const SignatureModal = (props: {
  isOpen: boolean;
  openLinkModal: () => void;
  isEdit: boolean;
  instructionLink: InstructionLink;
  onLinkFileChange: any;
  link: string;
  onAddSignature: (signature: Signature) => void;
  onFilterPersonChanged: any;
  pmolId: string;
}) => {
  const inputFile: any = useRef(null);
  const signPad: any = useRef(null);
  const { t } = useTranslation();
  const [isDraggable] = useBoolean(false);
  const titleId = useId('title');
  const ref: any = useRef();
  const [instructionLink, setLink] = useState(new InstructionLink());
  const [person, setPerson] = useState<IPersona[]>([]);
  const [signature, setSignature] = useState<Signature>(new Signature());
  const [isProgress, setProgress] = useState(false);
  const [clearDisable, setClearDisable] = useState(true);
  const [diableUpload, setDisableUpload] = useState(false);
  const [linkValidationMessages, setLinkValidationMsg] = useState<SignatureValidationMessages>(new SignatureValidationMessages());
  const [hasEdit, setHasEdit] = useState(false);
  const [disableSave, setDisableSave] = useState(true);

  const onChangeInput = (field: string) => (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string,
  ) => {
    setLink((prevState) => ({
      ...prevState,
      [field]: newValue,
      type: 'Website',
    }));
    setHasEdit(true);
    setLinkValidationState(field + 'ValidationMsg', '');
    if (field === 'value' && newValue!!.length > 0) {
      setDisableUpload(true);
    } else {
      setDisableUpload(false);
    }
    //handleSaveBtnDisable()
    if (
      field === 'title' &&
      newValue &&
      newValue !== '' &&
      instructionLink.value
    ) {
      setDisableSave(false);
    } else if (
      field === 'value' &&
      newValue &&
      newValue !== '' &&
      instructionLink.title
    ) {
      setDisableSave(false);
    } else {
      setDisableSave(true);
    }
  };

  const limitedSearchAdditionalProps: IBasePickerSuggestionsProps = {
    resultsMaximumNumber: 10,
    searchingText: 'Searching...',
  };

  let selectedPerson: IPersona[] = [];
  const onPersonSelected = (item: any) => {
    if (item && item.person) {
      selectedPerson = [
        {
          key: item.person.id,
          text: item.person.fullName,
        },
      ];
      setPerson(selectedPerson);
      setSignature((prevState) => ({
        ...prevState,
        name: item.person.fullName,
        cabId: item.person.id,
      }));
      if (signature.link) {
        setDisableSave(false);
      }
    }
    return item;
  };

  useEffect(() => {
    if (props.isEdit) {
      //setLink(props.instructionLink);
      setLink({
        type: props.instructionLink.type,
        value: props.instructionLink.value
          ? props.instructionLink.value
          : props.link,
        title: props.instructionLink.title,
        id: props.instructionLink.id,
        pbsInstructionId: props.instructionLink.pbsInstructionId,
        link: props.instructionLink.link,
      });
      setDisableUpload(true);
      setDisableSave(false);
    } else {
      setLink(new InstructionLink());
      setLinkValidationMsg(new SignatureValidationMessages());
      setDisableUpload(false);
      setDisableSave(true);
    }
  }, [props.instructionLink]);

  useEffect(() => {
    if (props.isEdit) {
      setLink((prevState) => ({
        ...prevState,
        value: props.link,
      }));
      setProgress(false);
      setLinkValidationState('value' + 'ValidationMsg', '');
      handleSaveBtnDisable();
    } else {
      if (props.link) {
        setLink((prevState) => ({
          ...prevState,
          value: props.link,
        }));
        setSignature((prevState) => ({
          ...prevState,
          link: props.link,
        }));
        setProgress(false);
        setLinkValidationState('value' + 'ValidationMsg', '');
        setDisableUpload(true);
        if (signature.name && props.link) {
          setDisableSave(false);
        }
      } else {
        setDisableUpload(false);
        setLink(new InstructionLink());
        handleSaveBtnDisable(true);
        setLinkValidationMsg(new SignatureValidationMessages());
      }
    }
  }, [props.link]);

  useEffect(() => {
    setSignature((prevState) => ({
      ...prevState,
      pmolId: props.pmolId,
    }));
  }, [props.pmolId]);

  useEffect(() => {
    if (instructionLink && ref.current && ref.current.criteria) {
      //validaErrorOnvalueChange('criteria');
    }
    ref.current = instructionLink;
  }, [instructionLink]);

  const validateSignature = (signature: Signature) => {
    //let isValidType = validateLinkType(link);
    let isPMOLidAvailable = signature.pmolId ? true : false;
    let isValidName = validateSignatureName(signature);
    let isValidSignature = validateSignatureValue(signature);
    return isValidName && isValidSignature && isPMOLidAvailable;
  };

  const validateSignatureValue = (signature: Signature) => {
    let isValidName = false;
    if (signature && signature.link) {
      isValidName = true;
      setLinkValidationState('linkValidationMsg', '');
    } else {
      isValidName = false;
      setLinkValidationState('linkValidationMsg', t('signatureRequired'));
    }
    return isValidName;
  };

  const handleSaveBtnDisable = (isDisable = false) => {
    if (instructionLink.title && instructionLink.value) {
      setDisableSave(false);
    } else if (!instructionLink.title || !instructionLink.value) {
      setDisableSave(true);
    }
    if (isDisable) {
      setDisableSave(true);
    }
  };

  const onInputChange = (input: string): string => {
    const outlookRegEx = /<.*>/g;
    const emailAddress = outlookRegEx.exec(input);

    if (emailAddress && emailAddress[0]) {
      return emailAddress[0].substring(1, emailAddress[0].length - 1);
    }

    return input;
  };

  const validateInput = (input: string): ValidationState => {
    if (input.indexOf('@') !== -1) {
      return ValidationState.valid;
    } else if (input.length > 1) {
      return ValidationState.warning;
    } else {
      return ValidationState.invalid;
    }
  };


  const validateSignatureName = (signature: Signature) => {
    let isValidLink = false;
    if (signature && signature.name) {
      isValidLink = true;
      setLinkValidationState('nameValidationMsg', '');
    } else {
      isValidLink = false;
      setLinkValidationState('nameValidationMsg', t('nameRequired'));
    }
    return isValidLink;
  };

  const setLinkValidationState = (key: string, msg: string) => {
    setLinkValidationMsg((prevState) => ({
      ...prevState,
      [key]: msg,
    }));
  };

  return (
    <div>
      <Modal
        titleAriaId={titleId}
        isOpen={props.isOpen}
        onDismiss={() => {
          props.openLinkModal();
          setTimeout(() => {
            setLinkValidationMsg(new SignatureValidationMessages());
          }, 1000);
        }}
        scrollableContentClassName="body"
        isBlocking={true}
        dragOptions={isDraggable ? dragOptions : undefined}
      >
        <div className={contentStyles.header}>
          <span id={titleId}>{t('Add Signature')}</span>
          <IconButton
            styles={cancelIconButtonStyles}
            iconProps={cancelIcon}
            ariaLabel="Close popup modal"
            onClick={() => {
              props.openLinkModal();
              setPerson([]);
              setDisableSave(true);
              setSignature(new Signature());
              setLink(new InstructionLink());
              setClearDisable(true);
              setTimeout(() => {
                setLinkValidationMsg(new SignatureValidationMessages());
              }, 1000);
            }}
          />
        </div>
        <ProgressBar show={isProgress}/>
        <div className={contentStyles.body}>
          <div className="proj-detail-content inner">
            <div className="ms-Grid-row">
              <Label>1. {t('signature')} </Label>

              <Stack horizontal wrap styles={stackStyles} tokens={stackTokens}>
                <Stack.Item grow={6} styles={stackItemStyles}>
                  <div className={`${classNames.fullWidth}`}>
                    <Label required={false}>{t('name')}</Label>
                    <NormalPeoplePicker
                      onChange={(items) => {
                        if (items && _.isArray(items) && items.length === 0) {
                          setPerson([]);
                          setDisableSave(false);
                          setSignature((prevState) => ({
                            ...prevState,
                            name: null,
                          }));
                          // setRisk((prevState) => ({
                          //   ...prevState,
                          //   cabPersonId: null,
                          // }));
                        } else {
                          if (!signPad?.current?.isEmpty()) {
                            setDisableSave(false);
                          }
                        }
                      }}
                      selectedItems={
                        person && person[0] && person[0] ? person : []
                      }
                      onResolveSuggestions={props.onFilterPersonChanged}
                      onItemSelected={onPersonSelected}
                      pickerSuggestionsProps={limitedSearchAdditionalProps}
                      className={'ms-PeoplePicker'}
                      key={'normal'}
                      itemLimit={1}
                      onValidateInput={validateInput}
                      removeButtonAriaLabel={'Remove'}
                      inputProps={{
                        onBlur: () => {
                        },
                        onFocus: () => {
                        },
                        'aria-label': 'People Picker',
                      }}
                      onInputChange={onInputChange}
                      resolveDelay={300}
                      disabled={false}
                    />
                  </div>
                </Stack.Item>
              </Stack>

              <Stack horizontal wrap styles={stackStyles} tokens={stackTokens}>
                <Stack.Item styles={signPadItemStyles}>
                  <Stack
                    horizontal
                    wrap
                    styles={stackStyles}
                    tokens={{
                      childrenGap: 5,
                      padding: 5,
                    }}
                  >
                    <Label>{t('signBelow')}</Label>
                    <SignatureCanvas
                      penColor="black"
                      canvasProps={{
                        width: 750,
                        height: 200,
                        className: 'sigCanvas',
                      }}
                      dotSize={1}
                      ref={signPad}
                      onEnd={() => {
                        if (!signPad?.current?.isEmpty()) {
                          setClearDisable(false);
                          if (signature.name) {
                            setDisableSave(false);
                          }
                          setSignature((prevState) => ({
                            ...prevState,
                            link: signPad?.current
                              ?.getTrimmedCanvas()
                              .toDataURL('image/png'),
                          }));
                        }
                      }}
                    />
                    <ActionButton
                      allowDisabledFocus
                      disabled={clearDisable}
                      checked={false}
                      onClick={() => {
                        signPad?.current?.clear();
                        setClearDisable(true);
                        setSignature((prevState) => ({
                          ...prevState,
                          link: null,
                        }));
                        setDisableSave(true);
                      }}
                    >
                      {t('clear')}
                    </ActionButton>
                  </Stack>
                </Stack.Item>
              </Stack>

              <Stack horizontal wrap styles={stackStyles} tokens={stackTokens}>
                <Stack.Item
                  grow={6}
                  styles={{
                    root: {
                      display: 'flex',
                      height: 30,
                      paddingBottom: 0,
                      flexGrow: 1,
                      width: 0,
                    },
                  }}
                >
                  {!(instructionLink && instructionLink.value) && (
                    <div className={`${classNames.fullWidth}`}>
                      <TextField
                        readOnly={true}
                        borderless={true}
                        style={{ height: 25 }}
                        value={'OR'}
                        disabled={
                          instructionLink && instructionLink.value
                            ? true
                            : false
                        }
                      />
                    </div>
                  )}
                </Stack.Item>
              </Stack>

              <Stack horizontal wrap styles={stackStyles} tokens={stackTokens}>
                <input
                  type="file"
                  id="file"
                  ref={inputFile}
                  style={{ display: 'none' }}
                  onChange={(event) => {
                    if (
                      event &&
                      event.target &&
                      event.target.files &&
                      event.target.files[0]
                    ) {
                      setLink((prevState) => ({
                        ...prevState,
                        type: getFileIconName(
                          event &&
                          event.target &&
                          event.target.files &&
                          event.target.files[0]
                            ? event.target.files[0].type
                            : null,
                        ),
                      }));
                      props.onLinkFileChange(event);
                    }
                    setProgress(true);
                  }}
                />

                <PrimaryButton
                  iconProps={addIconWhite}
                  text={t('upload')}
                  disabled={!clearDisable}
                  style={{ marginTop: 0, marginBottom: 30 }}
                  onClick={() => {
                    inputFile?.current?.click();
                  }}
                />

                <Image
                  src={instructionLink.value ? instructionLink.value : ''}
                  alt=""
                  width={100}
                  height={100}
                />
                {isProgress && (
                  <Spinner
                    size={SpinnerSize.large}
                    style={{ alignSelf: 'flex-start', marginLeft: -60 }}
                  />
                )}
              </Stack>
            </div>
          </div>
        </div>
        <div className={contentStyles.footer}>
          <PrimaryButton
            iconProps={addIconWhite}
            text={t('save')}
            style={{ marginTop: 20, marginBottom: 30 }}
            disabled={disableSave}
            onClick={() => {
              setSignature((prevState) => ({
                ...prevState,
                pmolId: props.pmolId,
              }));
              if (validateSignature(signature)) {
                props.onAddSignature(signature);
                setPerson([]);
                setSignature(new Signature());
                setDisableSave(true);
                setLink(new InstructionLink());
                setClearDisable(true);
              }
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default SignatureModal;
